import logo from './logo.png';
import React, { useState, useEffect } from "react";
import { db, auth, googleProvider } from "./firebase";
import {
  doc, setDoc, getDoc, getDocs, collection, query, orderBy
} from "firebase/firestore";
import {
  signInWithPopup, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, signOut, onAuthStateChanged
} from "firebase/auth";
import {
  HomeScreen, CreatePlanScreen, WorkoutScreen,
  RunScreen, HistoryScreen, ProgressScreen
} from "./Screens";
import { appWrap, btnStyle, backBtn, inputStyle, labelStyle } from "./styles";
import BodyWeightTracker from "./components/BodyWeightTracker";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [screen, setScreen] = useState("home");
  const [plans, setPlans] = useState([]);
  const [activePlanIndex, setActivePlanIndex] = useState(0);
  const [activeDay, setActiveDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingPlanIndex, setEditingPlanIndex] = useState(null);
  const [lastTrained, setLastTrained] = useState({});

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const loadData = async () => {
      setLoading(true);
      const snap = await getDoc(doc(db, "users", user.uid, "settings", "workoutPlans"));
      if (snap.exists() && snap.data().plans) {
        setPlans(snap.data().plans);
      } else {
        setPlans([]);
      }
      const wSnap = await getDocs(query(collection(db, "users", user.uid, "workouts"), orderBy("date", "desc")));
      const seen = {};
      wSnap.docs.forEach(d => {
        const { dayName, date } = d.data();
        if (dayName && !seen[dayName] && date) {
          seen[dayName] = date.toDate().toLocaleDateString("no-NO", { day: "numeric", month: "short" });
        }
      });
      setLastTrained(seen);
      setLoading(false);
    };
    loadData();
  }, [user]);

  const savePlans = async (newPlans) => {
    await setDoc(doc(db, "users", user.uid, "settings", "workoutPlans"), { plans: newPlans });
    setPlans(newPlans);
  };

  const handleSavePlan = (newPlan) => {
    const updated = [...plans];
    if (editingPlanIndex !== null) {
      updated[editingPlanIndex] = newPlan;
    } else {
      updated.push(newPlan);
      setActivePlanIndex(updated.length - 1);
    }
    savePlans(updated);
    setScreen("home");
  };

  const handleDeletePlan = (index) => {
    const updated = plans.filter((_, i) => i !== index);
    savePlans(updated);
    setActivePlanIndex(0);
    setScreen("home");
  };

  const refreshLastTrained = async () => {
    const wSnap = await getDocs(query(collection(db, "users", user.uid, "workouts"), orderBy("date", "desc")));
    const seen = {};
    wSnap.docs.forEach(d => {
      const { dayName, date } = d.data();
      if (dayName && !seen[dayName] && date) {
        seen[dayName] = date.toDate().toLocaleDateString("no-NO", { day: "numeric", month: "short" });
      }
    });
    setLastTrained(seen);
  };

  if (authLoading) return (
    <div style={appWrap}>
      <p style={{ color: "#9CA3AF", padding: 24, textAlign: "center" }}>Loading...</p>
    </div>
  );

  if (!user) return <LoginScreen />;

  if (loading) return (
    <div style={appWrap}>
      <p style={{ color: "#9CA3AF", padding: 24, textAlign: "center" }}>Loading...</p>
    </div>
  );

  const activePlan = plans[activePlanIndex] || null;

  return (
    <div style={appWrap}>
      <PWAInstallPrompt />
      <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid #1F2937", marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ fontSize: 24, fontWeight: "800", margin: 0, color: "#F9FAFB", letterSpacing: "-0.5px", display: "flex", alignItems: "center", gap: 10 }}>
          <img src={logo} alt="MyGainLog" style={{ width: 32, height: 32, objectFit: "contain" }} />
          MyGainLog
        </h1>
        <button onClick={() => signOut(auth)} style={{ background: "none", border: "1px solid #374151", color: "#6B7280", cursor: "pointer", fontSize: 12, borderRadius: 8, padding: "4px 10px" }}>
          Sign out
        </button>
      </div>
      <div style={{ padding: "16px 24px" }}>
        {screen === "home" && (
          <HomeScreen
            userId={user.uid}
            plans={plans}
            activePlanIndex={activePlanIndex}
            lastTrained={lastTrained}
            onSelectPlan={(i) => setActivePlanIndex(i)}
            onNewPlan={() => { setEditingPlanIndex(null); setScreen("createPlan"); }}
            onEditPlan={() => { setEditingPlanIndex(activePlanIndex); setScreen("createPlan"); }}
            onDeletePlan={handleDeletePlan}
            onStartDay={(day) => { setActiveDay(day); setScreen("plannedWorkout"); }}
            onCustomWorkout={() => setScreen("chooseLocation")}
            onRun={() => setScreen("run")}
            onHistory={() => setScreen("history")}
            onBodyWeight={() => setScreen("bodyweight")}
          />
        )}
        {screen === "createPlan" && (
          <CreatePlanScreen
            existingPlan={editingPlanIndex !== null ? plans[editingPlanIndex] : null}
            onSave={handleSavePlan}
            onBack={() => setScreen("home")}
          />
        )}
        {screen === "plannedWorkout" && (
          <WorkoutScreen
            userId={user.uid}
            location={activeDay?.location || "Gym"}
            preloadedExercises={activeDay?.exercises || []}
            dayName={activeDay?.name}
            onBack={() => { refreshLastTrained(); setScreen("home"); }}
          />
        )}
        {screen === "chooseLocation" && (
          <div>
            <button onClick={() => setScreen("home")} style={backBtn}>← Back</button>
            <h2 style={{ color: "#F9FAFB", marginBottom: 20 }}>Where are you training?</h2>
            <button onClick={() => { setActiveDay({ name: null, location: "Gym", exercises: [] }); setScreen("plannedWorkout"); }} style={btnStyle("#F97316")}>🏋️ Gym</button>
            <button onClick={() => { setActiveDay({ name: null, location: "Home", exercises: [] }); setScreen("plannedWorkout"); }} style={btnStyle("#7C3AED")}>🏠 Home</button>
          </div>
        )}
        {screen === "run" && <RunScreen userId={user.uid} onBack={() => setScreen("home")} />}
        {screen === "history" && <HistoryScreen userId={user.uid} onBack={() => setScreen("home")} onProgress={() => setScreen("progress")} />}
        {screen === "progress" && <ProgressScreen userId={user.uid} onBack={() => setScreen("history")} />}
        {screen === "bodyweight" && <BodyWeightTracker onBack={() => setScreen("home")} />}
      </div>
    </div>
  );
}

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
function LoginScreen() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    setLoading(true); setError("");
    try { await signInWithPopup(auth, googleProvider); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleEmail = async () => {
    if (!email || !password) { setError("Please fill in email and password"); return; }
    setLoading(true); setError("");
    try {
      if (mode === "login") await signInWithEmailAndPassword(auth, email, password);
      else await createUserWithEmailAndPassword(auth, email, password);
    } catch (e) {
      setError(e.code === "auth/wrong-password" || e.code === "auth/user-not-found"
        ? "Invalid email or password" : e.code === "auth/email-already-in-use"
        ? "Account already exists — try logging in" : e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ ...appWrap, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <div style={{ width: "100%", maxWidth: 400, padding: "0 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img src={logo} alt="MyGainLog" style={{ width: 120, height: 120, objectFit: "contain", marginBottom: 8 }} />
          <h1 style={{ fontSize: 28, fontWeight: "800", color: "#F9FAFB", margin: 0 }}>MyGainLog</h1>
          <p style={{ color: "#6B7280", marginTop: 8, fontSize: 14 }}>Track your gains. Own your progress.</p>
        </div>
        <button onClick={handleGoogle} disabled={loading} style={{
          width: "100%", background: "white", color: "#111827", border: "none",
          borderRadius: 12, padding: "13px 0", cursor: "pointer", fontSize: 15,
          fontWeight: "700", marginBottom: 16, display: "flex", alignItems: "center",
          justifyContent: "center", gap: 10
        }}>
          <img src="https://www.google.com/favicon.ico" alt="" style={{ width: 18, height: 18 }} />
          Continue with Google
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: "#374151" }} />
          <span style={{ color: "#6B7280", fontSize: 13 }}>or</span>
          <div style={{ flex: 1, height: 1, background: "#374151" }} />
        </div>
        <label style={labelStyle}>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com" style={{ ...inputStyle, marginBottom: 12 }} />
        <label style={labelStyle}>Password</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleEmail()}
          placeholder="••••••••" style={{ ...inputStyle, marginBottom: 16 }} />
        {error && <p style={{ color: "#EF4444", fontSize: 13, marginBottom: 12 }}>{error}</p>}
        <button onClick={handleEmail} disabled={loading} style={btnStyle("#4F46E5")}>
          {loading ? "..." : mode === "login" ? "Sign In" : "Create Account"}
        </button>
        <p style={{ textAlign: "center", color: "#6B7280", fontSize: 14, marginTop: 16 }}>
          {mode === "login" ? "No account? " : "Already have one? "}
          <span onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            style={{ color: "#4F46E5", cursor: "pointer", fontWeight: "700" }}>
            {mode === "login" ? "Create one" : "Sign in"}
          </span>
        </p>
      </div>
    </div>
  );
}

export default App;